// @owners { team: patients }
import { useContext } from 'react';
import { getShipmentWithID } from '~shared/features/shipments/selectors/getShipments';
import { useSelectorShared } from '~shared/store';
import { AddOnsContext } from '../../checkout/components/essentials/AddOnsContext';
import { useQueryFeaturedOtcs } from '../queries/useQueryFeaturedOtcs';

/**
 * Featured OTCs for the shipment currently in `AddOnsContext`, and whether there is anything worth
 * rendering for it.
 *
 * Single source of truth for that decision: the carousel and the section that frames it both read
 * from here, so a separator can never end up on screen without the content it is separating.
 */
export const useFeaturedOtcsForShipment = () => {
  // Reads the context directly rather than through the `useAddOnsContext` hook, which wires up
  // navigation, analytics, toasts, the query client, a dispatch and order pricing. All this needs is
  // the shipment id.
  const { shipmentID } = useContext(AddOnsContext);
  const shipment = useSelectorShared((state) => getShipmentWithID(state, { shipmentID }));
  const { error, isLoading, products } = useQueryFeaturedOtcs({
    addressID: shipment?.address_id,
    date: shipment?.scheduled_date,
  });

  return {
    error,
    isLoading,
    products,
    /**
     * `products` is undefined both while the query is in flight and once it resolves empty, so this
     * is only ever true when there are products actually in hand.
     */
    hasProducts: !error && !!products,
  };
};
